import React, { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const TABS = [
  { id: 'integrations', label: 'Venue integrations' },
  { id: 'storage', label: 'Tenant storage' },
  { id: 'functions', label: 'Functions' },
  { id: 'interface', label: 'Interface data' },
  { id: 'devices', label: 'Gateway devices' },
]

/** venues.* columns returned in gateway system_config / azure_config */
const VENUE_INTEGRATION_FIELDS = [
  ['membership_system', 'Membership system'],
  ['gaming_system', 'Gaming system'],
  ['visitor_management_system', 'Visitor management system'],
  ['pig_azure_relay_uri', 'PiG relay URI (HTTPS)'],
  ['pig_azure_relay_entity', 'PiG relay entity (WCF)'],
  ['pig_azure_relay_auth_enabled', 'PiG relay auth enabled (0/1)'],
  ['pig_azure_relay_issuer_name', 'PiG relay issuer name'],
  ['pig_azure_relay_issuer_secret', 'PiG relay issuer secret'],
  ['pig_command_api_path', 'PiG command API path'],
  ['pig_encryption_enabled', 'PiG encryption enabled (0/1)'],
  ['azure_service_bus_uri', 'Legacy Azure bus URI (dual-write)'],
  ['azure_service_bus_issuer_name', 'Legacy Azure issuer name'],
  ['azure_service_bus_issuer_secret', 'Legacy Azure issuer secret'],
  ['sid_storage_conn_string', 'SID storage connection string (gateway)'],
]

function isSecretField(key) {
  return key.includes('secret') || key.includes('conn_string')
}

function apiBase(groupId) {
  return getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}/psg-gateway`)
}

/** Standalone route — redirects into tenant workspace. */
export default function TenantPsgGateway() {
  const [searchParams] = useSearchParams()
  const tenant = searchParams.get('tenant')
  if (tenant) return <Navigate to={`/admin/tenants/${encodeURIComponent(tenant)}/psg-gateway`} replace />
  return <Navigate to="/admin/tenants" replace />
}

export function TenantPsgGatewayPanel({ groupId, embedded = false }) {
  const [tab, setTab] = useState('integrations')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [saving, setSaving] = useState(false)
  const [editVenueId, setEditVenueId] = useState(null)
  const [integrationForm, setIntegrationForm] = useState({})
  const [storageForm, setStorageForm] = useState({ blob_container: '', blob_connection_string: '' })
  const [fnForm, setFnForm] = useState(null)
  const [ifaceForm, setIfaceForm] = useState(null)

  const load = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(apiBase(groupId))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || json.message || 'Load failed')
      setData(json.data)
      const ts = json.data?.tenant_blob_storage || {}
      setStorageForm({
        blob_container: ts.blob_container ?? '',
        blob_connection_string: ts.blob_connection_string_set ? '********' : '',
      })
    } catch (e) {
      setError(e.message || 'Failed to load')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    load()
  }, [load])

  const startEditVenue = (v) => {
    setEditVenueId(v.venue_id)
    setIntegrationForm({
      membership_system: v.membership_system ?? '',
      gaming_system: v.gaming_system ?? '',
      visitor_management_system: v.visitor_management_system ?? '',
      pig_azure_relay_uri: v.pig_azure_relay_uri ?? v.azure_service_bus_uri ?? '',
      pig_azure_relay_entity: v.pig_azure_relay_entity ?? '',
      pig_azure_relay_auth_enabled: v.pig_azure_relay_auth_enabled ?? '1',
      pig_azure_relay_issuer_name: v.pig_azure_relay_issuer_name ?? v.azure_service_bus_issuer_name ?? '',
      pig_azure_relay_issuer_secret:
        v.pig_azure_relay_issuer_secret_set || v.azure_service_bus_issuer_secret_set ? '********' : '',
      pig_command_api_path: v.pig_command_api_path ?? 'PerformPiGCommand',
      pig_encryption_enabled: v.pig_encryption_enabled ?? '0',
      azure_service_bus_uri: v.azure_service_bus_uri ?? '',
      azure_service_bus_issuer_name: v.azure_service_bus_issuer_name ?? '',
      azure_service_bus_issuer_secret: v.azure_service_bus_issuer_secret_set ? '********' : '',
      sid_storage_conn_string: v.sid_storage_conn_string_set ? '********' : '',
    })
  }

  const saveIntegrations = async () => {
    if (!editVenueId) return
    setSaving(true)
    try {
      const body = { ...integrationForm }
      if (body.azure_service_bus_issuer_secret === '********') delete body.azure_service_bus_issuer_secret
      if (body.pig_azure_relay_issuer_secret === '********') delete body.pig_azure_relay_issuer_secret
      if (body.sid_storage_conn_string === '********') delete body.sid_storage_conn_string
      const res = await authFetch(`${apiBase(groupId)}/venues/${encodeURIComponent(editVenueId)}/integrations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      toast.success('Venue integrations saved')
      setEditVenueId(null)
      await load()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const saveStorageConfig = async () => {
    setSaving(true)
    try {
      const body = { ...storageForm }
      if (body.blob_connection_string === '********') delete body.blob_connection_string
      const res = await authFetch(`${apiBase(groupId)}/storage-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      toast.success('Tenant storage configuration saved')
      await load()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const saveFunction = async () => {
    if (!fnForm || !groupId) return
    setSaving(true)
    try {
      const isEdit = !!fnForm.id
      const url = isEdit
        ? `${apiBase(groupId)}/functions/${fnForm.id}`
        : `${apiBase(groupId)}/functions`
      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fnForm),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      setFnForm(null)
      await load()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const saveInterface = async () => {
    if (!ifaceForm || !groupId) return
    setSaving(true)
    try {
      const isEdit = !!ifaceForm.id
      const url = isEdit
        ? `${apiBase(groupId)}/interface-data/${ifaceForm.id}`
        : `${apiBase(groupId)}/interface-data`
      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ifaceForm),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      setIfaceForm(null)
      await load()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const counts = data?.counts || {}
  const venues = data?.venues || []
  const devices = data?.devices || []
  const functions = data?.functions || []
  const interfaceData = data?.interface_data || []
  const defaultDeviceId = devices[0]?.device_id || functions[0]?.device_id || ''

  if (!groupId) return null

  return (
    <div className={embedded ? '' : 'max-w-6xl mx-auto px-4 py-6'}>
      {!embedded && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <ServerStackIcon className="h-8 w-8 text-teal-700" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">PSG Gateway</h1>
              <p className="text-sm text-gray-600">Tenant-wide gateway config</p>
            </div>
          </div>
          <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      )}
      {embedded && counts && (
        <p className="text-xs text-gray-500 mb-3">
          {counts.venues} venues · {counts.devices} gateway devices · {counts.functions} functions ·{' '}
          {counts.interface_data} interface rows
          <button type="button" onClick={load} className="ml-2 text-teal-700 underline">Refresh</button>
        </p>
      )}
      {error && <div className="mb-4 p-3 rounded bg-red-50 text-red-800 text-sm">{error}</div>}
      {
        <>
          <div className="flex border-b gap-1 mb-4">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  tab === t.id ? 'border-teal-600 text-teal-800' : 'border-transparent text-gray-600'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading && !data ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : (
            <>
              {tab === 'integrations' && (
                <div className="space-y-4">
                  {!data?.schema?.venue_psg_columns?.length ? (
                    <p className="text-sm text-amber-800 bg-amber-50 p-3 rounded">PSG venue columns missing — run tenant migration 088.</p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        Per-venue values on <code className="text-xs bg-gray-100 px-1 rounded">venues</code> returned to the
                        gateway as <strong>system_config</strong> and <strong>azure_config</strong> (via device{' '}
                        <code className="text-xs">venue_id_at_register</code>).
                      </p>
                      <div className="overflow-x-auto border rounded">
                        <table className="min-w-full text-sm divide-y">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left">Venue</th>
                              <th className="px-3 py-2 text-left">Name</th>
                              <th className="px-3 py-2 text-left">Membership</th>
                              <th className="px-3 py-2 text-left">Gaming</th>
                              <th className="px-3 py-2 text-left">Visitor Mgmt</th>
                              <th className="px-3 py-2 text-left">PiG relay</th>
                              <th className="px-3 py-2 text-left">SID storage</th>
                              <th className="px-3 py-2">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {venues.map((v) => (
                              <tr key={v.venue_id} className={editVenueId === v.venue_id ? 'bg-teal-50/40' : ''}>
                                <td className="px-3 py-2 font-mono text-xs">{v.venue_id}</td>
                                <td className="px-3 py-2">{v.name || '—'}</td>
                                <td className="px-3 py-2">{v.membership_system || '—'}</td>
                                <td className="px-3 py-2">{v.gaming_system || '—'}</td>
                                <td className="px-3 py-2">{v.visitor_management_system || '—'}</td>
                                <td className="px-3 py-2">
                                  {v.pig_azure_relay_uri || v.azure_service_bus_uri ? (
                                    <span className="text-emerald-700 text-xs">set</span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {v.sid_storage_conn_string_set ? (
                                    <span className="text-emerald-700 text-xs">set</span>
                                  ) : (
                                    <span className="text-amber-700 text-xs">missing</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <button
                                    type="button"
                                    className="text-teal-700 text-xs font-medium"
                                    onClick={() => startEditVenue(v)}
                                  >
                                    Edit
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  {editVenueId && (
                    <div className="border rounded p-4 bg-gray-50 space-y-3">
                      <p className="text-sm font-medium">Edit venue integrations — {editVenueId}</p>
                      <p className="text-xs text-gray-500">
                        Secrets show as masked when already set. Leave blank to keep; enter a new value to rotate.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {VENUE_INTEGRATION_FIELDS.map(([key, label]) => (
                          <div key={key}>
                            <label className="block text-xs text-gray-600 mb-1">{label}</label>
                            <input
                              className="input-field w-full text-sm font-mono"
                              type={isSecretField(key) ? 'password' : 'text'}
                              value={integrationForm[key] ?? ''}
                              onChange={(e) => setIntegrationForm((p) => ({ ...p, [key]: e.target.value }))}
                              placeholder={isSecretField(key) ? 'Leave blank to keep existing' : ''}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button type="button" disabled={saving} onClick={saveIntegrations} className="px-3 py-1.5 bg-teal-700 text-white rounded text-sm disabled:opacity-50">
                          Save venue integrations
                        </button>
                        <button type="button" onClick={() => setEditVenueId(null)} className="px-3 py-1.5 bg-gray-200 rounded text-sm">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === 'storage' && (
                <div className="space-y-4 max-w-2xl">
                  <p className="text-sm text-gray-600">
                    PaySafeGO operational blob storage for this tenant — stored in{' '}
                    <code className="text-xs bg-gray-100 px-1 rounded">venue_configuration</code> (group scope, category{' '}
                    <code className="text-xs">azure</code>). Used for KYC images, incident photos, branding blobs, etc.
                  </p>
                  <p className="text-xs text-gray-500">
                    Separate from per-venue <strong>SID storage connection string</strong> on the Venue integrations tab
                    (gateway <code className="text-xs">azure_config</code> for PiG / PSG Service).
                  </p>
                  {data?.tenant_blob_storage?.env_fallback_available && !data?.tenant_blob_storage?.blob_connection_string_set ? (
                    <p className="text-xs text-amber-800 bg-amber-50 p-2 rounded">
                      No tenant row yet — API may fall back to server <code className="text-xs">AZURE_STORAGE_CONNECTION_STRING</code>.
                    </p>
                  ) : null}
                  <div className="space-y-3 border rounded p-4 bg-gray-50">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Blob container name</label>
                      <input
                        className="input-field w-full font-mono text-sm"
                        value={storageForm.blob_container ?? ''}
                        onChange={(e) => setStorageForm((p) => ({ ...p, blob_container: e.target.value }))}
                        placeholder="e.g. psgdev-paysafego"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Azure storage connection string</label>
                      <input
                        className="input-field w-full font-mono text-sm"
                        type="password"
                        value={storageForm.blob_connection_string ?? ''}
                        onChange={(e) => setStorageForm((p) => ({ ...p, blob_connection_string: e.target.value }))}
                        placeholder={
                          data?.tenant_blob_storage?.blob_connection_string_set
                            ? 'Leave blank to keep existing'
                            : 'DefaultEndpointsProtocol=https;AccountName=…'
                        }
                      />
                    </div>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={saveStorageConfig}
                      className="px-4 py-2 rounded-md bg-teal-700 text-white text-sm hover:bg-teal-800 disabled:opacity-50"
                    >
                      Save tenant storage
                    </button>
                  </div>
                </div>
              )}

              {tab === 'functions' && (
                <div className="space-y-3">
                  <button
                    type="button"
                    className="text-sm px-3 py-1 rounded bg-blue-100 text-blue-800"
                    onClick={() =>
                      setFnForm({
                        device_id: defaultDeviceId,
                        function_code: '',
                        interface_type: 'IGT',
                        function_flag: '1',
                        schedule_time: '06:00:00',
                        file_prefix: '',
                        field_delimiter: 'PIPE',
                      })
                    }
                  >
                    <PlusIcon className="h-4 w-4 inline" /> Add function
                  </button>
                  {fnForm && (
                    <div className="border rounded p-3 bg-gray-50 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {['device_id', 'function_code', 'interface_type', 'schedule_time', 'file_prefix'].map((k) => (
                        <div key={k}>
                          <label className="text-xs text-gray-500">{k}</label>
                          <input className="input-field w-full text-xs font-mono" value={fnForm[k] ?? ''} onChange={(e) => setFnForm((p) => ({ ...p, [k]: e.target.value }))} />
                        </div>
                      ))}
                      <div className="col-span-full flex gap-2">
                        <button type="button" onClick={saveFunction} className="px-3 py-1 bg-teal-700 text-white rounded text-sm">
                          {fnForm.id ? 'Update' : 'Create'}
                        </button>
                        <button type="button" onClick={() => setFnForm(null)} className="px-3 py-1 bg-gray-200 rounded text-sm">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <FunctionsTable functions={functions} onEdit={setFnForm} onDelete={async (id) => {
                    if (!confirm('Delete?')) return
                    await authFetch(`${apiBase(groupId)}/functions/${id}`, { method: 'DELETE' })
                    await load()
                  }} />
                </div>
              )}

              {tab === 'interface' && (
                <div className="space-y-3">
                  <button
                    type="button"
                    className="text-sm px-3 py-1 rounded bg-blue-100 text-blue-800"
                    onClick={() =>
                      setIfaceForm({ interface_type: 'MEMBERSHIP', interface_name: 'IGT', name: 'ExtractFilePath', value: '', device_id: null })
                    }
                  >
                    <PlusIcon className="h-4 w-4 inline" /> Add row
                  </button>
                  {ifaceForm && (
                    <div className="border rounded p-3 bg-gray-50 grid grid-cols-2 gap-2">
                      {['interface_type', 'interface_name', 'name', 'value', 'device_id'].map((k) => (
                        <div key={k} className={k === 'value' ? 'col-span-2' : ''}>
                          <label className="text-xs text-gray-500">{k}</label>
                          <input
                            className="input-field w-full text-sm"
                            value={ifaceForm[k] ?? ''}
                            onChange={(e) => setIfaceForm((p) => ({ ...p, [k]: e.target.value === '' && k === 'device_id' ? null : e.target.value }))}
                          />
                        </div>
                      ))}
                      <div className="col-span-2 flex gap-2">
                        <button type="button" onClick={saveInterface} className="px-3 py-1 bg-teal-700 text-white rounded text-sm">
                          {ifaceForm.id ? 'Update' : 'Create'}
                        </button>
                        <button type="button" onClick={() => setIfaceForm(null)} className="px-3 py-1 bg-gray-200 rounded text-sm">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <InterfaceTable rows={interfaceData} onEdit={setIfaceForm} onDelete={async (id) => {
                    if (!confirm('Delete?')) return
                    await authFetch(`${apiBase(groupId)}/interface-data/${id}`, { method: 'DELETE' })
                    await load()
                  }} />
                </div>
              )}

              {tab === 'devices' && (
                <p className="text-sm text-gray-600 mb-2">
                  Gateway PCs for this tenant. Device approval and keys are on the <strong>Devices</strong> tab.
                </p>
              )}
              {tab === 'devices' && <DevicesTable devices={devices} />}
            </>
          )}
        </>
      }
    </div>
  )
}

function FunctionsTable({ functions, onEdit, onDelete }) {
  return (
    <div className="overflow-x-auto border rounded">
      <table className="min-w-full text-xs divide-y">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 text-left">device_id</th>
            <th className="px-2 py-2 text-left">function_code</th>
            <th className="px-2 py-2 text-left">schedule</th>
            <th className="px-2 py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {functions.map((row) => (
            <tr key={row.id}>
              <td className="px-2 py-1 font-mono">{String(row.device_id).slice(0, 8)}…</td>
              <td className="px-2 py-1">{row.function_code}</td>
              <td className="px-2 py-1">{row.schedule_time || '—'}</td>
              <td className="px-2 py-1">
                <button type="button" onClick={() => onEdit({ ...row })} className="text-blue-600 mr-1">
                  <PencilIcon className="h-4 w-4 inline" />
                </button>
                <button type="button" onClick={() => onDelete(row.id)} className="text-red-600">
                  <TrashIcon className="h-4 w-4 inline" />
                </button>
              </td>
            </tr>
          ))}
          {functions.length === 0 && (
            <tr>
              <td colSpan={4} className="px-2 py-6 text-center text-gray-500">
                No psg_functions rows in tenant database
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function InterfaceTable({ rows, onEdit, onDelete }) {
  return (
    <div className="overflow-x-auto border rounded">
      <table className="min-w-full text-xs divide-y">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 text-left">type</th>
            <th className="px-2 py-2 text-left">name</th>
            <th className="px-2 py-2 text-left">value</th>
            <th className="px-2 py-2 text-left">device</th>
            <th className="px-2 py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="px-2 py-1">{row.interface_type}</td>
              <td className="px-2 py-1">{row.name}</td>
              <td className="px-2 py-1 max-w-xs truncate">{row.value || '—'}</td>
              <td className="px-2 py-1 font-mono text-[10px]">{row.device_id ? `${String(row.device_id).slice(0, 8)}…` : 'tenant-wide'}</td>
              <td className="px-2 py-1">
                <button type="button" onClick={() => onEdit({ ...row })} className="text-blue-600 mr-1">
                  <PencilIcon className="h-4 w-4 inline" />
                </button>
                <button type="button" onClick={() => onDelete(row.id)} className="text-red-600">
                  <TrashIcon className="h-4 w-4 inline" />
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-2 py-6 text-center text-gray-500">
                No third_party_interface_data rows
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function DevicesTable({ devices }) {
  return (
    <div className="overflow-x-auto border rounded">
      <table className="min-w-full text-sm divide-y">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left">Device</th>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Venue at register</th>
            <th className="px-3 py-2 text-left">Approval</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => (
            <tr key={d.device_id}>
              <td className="px-3 py-2 font-mono text-xs">{d.device_id}</td>
              <td className="px-3 py-2">{d.device_type || '—'}</td>
              <td className="px-3 py-2 font-mono text-xs">{d.venue_id_at_register || '—'}</td>
              <td className="px-3 py-2">{d.approval_status || '—'}</td>
            </tr>
          ))}
          {devices.length === 0 && (
            <tr>
              <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                No gateway devices (device_type PSGGateway or payload key set)
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
